import React from 'react';
import { Helmet } from 'react-helmet-async';
import { PUBLIC_APP_ORIGIN } from '../../config/publicUrls';

// Mirrors weavewords-api's ShareRenderHandler headBlock: the server writes these tags for the
// first load (crawlers, link previews), and this renders the same set once the SPA is running.
// Keep the two in sync — utils/ssrPage.ts explains how they hand over, and utils/shareText.ts
// holds the formulas both sides share.

interface ShareHeadProps {
    title: string;
    description: string;
    url: string;
    noIndex?: boolean;
    jsonLd: object;
}

const ShareHead: React.FC<ShareHeadProps> = ({ title, description, url, noIndex, jsonLd }) => {
    const image = `${PUBLIC_APP_ORIGIN}/logo512.png`;
    return (
        <Helmet>
            <title>{title}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={url} />
            {noIndex && <meta name="robots" content="noindex" />}
            <meta property="og:type" content="website" />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={image} />
            <meta property="og:site_name" content="WeaveWords" />
            <meta name="twitter:card" content="summary" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />
            <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        </Helmet>
    );
};

// The head for a share page that can't be shown (revoked link, lookup failure): the same tags as
// the API's 404 page. No canonical — one pointing at the home page would contradict the noindex.
export const ShareNotFoundHead: React.FC = () => (
    <Helmet>
        <title>Not found | WeaveWords</title>
        <meta name="description" content="This shared page is no longer available." />
        <meta name="robots" content="noindex" />
    </Helmet>
);

export default ShareHead;
